import classNames from "classnames";
import Table from "components/DesignSystem/Table/V2/Table";
import { ComponentProps, ReactNode } from "react";
import {
  flexRender,
  HeaderGroup,
  Row,
  Table as TableProps,
} from "react-table-8.10.7";
import { TableUiBodyLoader } from "./TableUiBodyLoader";

export const TableUI = <T,>({
  sizeType = "percent",
  table,
  children,
  header,
  size,
  onRowClick,
  enableSort = false,
  layout,
  sticky = true,
  isHeaderCellUpperCase = true,
  verticalAlign,
  hideHeader = false,
  selectedCell,
  moreRows,
  height,
  isLoading,
  disabledRows = [],
  fillCellHeight = false,
  className,
  contentClassName,
}: {
  table: TableProps<T>;
  children?: ({ row }: { row: Row<T> }) => ReactNode;
  header?: ({
    headerGroup,
    index,
  }: {
    headerGroup: HeaderGroup<T>;
    index: number;
  }) => ReactNode;
  size?: "small" | "regular" | "large";
  onRowClick?: (row: Row<T>) => void;
  enableSort?: boolean;
  layout?: ComponentProps<typeof Table.Container>[`layout`];
  sticky?: boolean;
  isHeaderCellUpperCase?: boolean;
  verticalAlign?: "top" | "middle" | "bottom";
  hideHeader?: boolean;
  sizeType?: "percent" | "absolute";
  selectedCell?: string | null;
  moreRows?: ReactNode;
  height?: string | number;
  isLoading?: boolean;
  disabledRows?: string[];
  fillCellHeight?: boolean;
  className?: string;
  /** Applied to the <table> — use it to floor the width of a percent-sized
   * table so its columns stay readable and the container scrolls instead. */
  contentClassName?: string;
}) => {
  return (
    <Table.Container
      sticky={sticky}
      layout={layout}
      style={{ height }}
      className={className}
    >
      <Table.Content className={contentClassName}>
        {!hideHeader && (
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup, index) =>
              Boolean(header) ? (
                header?.({ headerGroup, index })
              ) : (
                <Table.HeadRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) =>
                    enableSort ? (
                      <Table.HeadCell
                        className="t-text-subtext-sm t-uppercase t-px-2 t-py-2 t-group"
                        onClick={header.column.getToggleSortingHandler()}
                        isUpperCase={isHeaderCellUpperCase}
                        key={header.id}
                        style={{
                          width:
                            sizeType === "percent"
                              ? `${header.getSize()}%`
                              : `${header.getSize()}px`,
                          minWidth:
                            sizeType === "absolute"
                              ? `${header.column.getSize()}px`
                              : "auto",
                        }}
                        {...(header.column.getCanSort()
                          ? { role: "button" }
                          : {})}
                        showDivider={Boolean(
                          //@ts-ignore
                          header.column.columnDef.meta?.showDivider
                        )}
                      >
                        <span className="t-flex t-gap-1 t-items-center">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {enableSort && header.column.getCanSort() && (
                            <Table.HeadCellSort
                              nextSortOrder={header.column.getNextSortingOrder()}
                              isSorted={header.column.getIsSorted()}
                            />
                          )}
                        </span>
                      </Table.HeadCell>
                    ) : (
                      <Table.HeadCell
                        colSpan={header.colSpan}
                        isUpperCase={isHeaderCellUpperCase}
                        key={header.id}
                        style={{
                          width:
                            sizeType === "percent"
                              ? `${header.getSize()}%`
                              : `${header.getSize()}px`,
                          minWidth:
                            sizeType === "absolute"
                              ? `${header.column.getSize()}px`
                              : "auto",
                        }}
                        showDivider={Boolean(
                          //@ts-ignore
                          header.column.columnDef.meta?.showDivider
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    )
                  )}
                </Table.HeadRow>
              )
            )}
          </Table.Head>
        )}
        {isLoading ? (
          <TableUiBodyLoader table={table} sizeType={sizeType} />
        ) : (
          <Table.Body>
            {table.getRowModel().rows.map((row) => {
              const isRowPinned = table
                ?.getState()
                ?.rowPinning.top?.includes(row.id);
              return Boolean(children) ? (
                children?.({ row })
              ) : (
                <Table.Row
                  onRowClick={onRowClick ? () => onRowClick(row) : undefined}
                  key={row.id}
                  disabled={disabledRows?.includes(row.id)}
                  className={classNames("", {
                    "t-bg-surface-lighter-grey": isRowPinned,
                  })}
                  isRowSelected={row.getIsSelected()}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      className={classNames({
                        "t-bg-neutral-0": selectedCell === cell.id,
                        "t-h-px": fillCellHeight,
                      })}
                      style={{
                        width:
                          sizeType === "percent"
                            ? `${cell.column.getSize()}%`
                            : `${cell.column.getSize()}px`,
                        verticalAlign,
                        minWidth:
                          sizeType === "absolute"
                            ? `${cell.column.getSize()}px`
                            : "auto",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
            {moreRows}
          </Table.Body>
        )}
      </Table.Content>
    </Table.Container>
  );
};
